import { useState } from 'react';
import { motion } from 'framer-motion';
import API from '../api';
import { useNavigate, Link } from 'react-router-dom';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const login = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const res = await API.post(
        '/login',
        new URLSearchParams({ username: email, password }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );
      localStorage.setItem('token', res.data.access_token);
      console.log('Login success, navigating...');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error.response || error);
      const errorMessage = error.response?.data?.detail || 'Login failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      style={{ 
        minHeight: '100vh', 
        background: '#111111',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}
    >
      {/* Animated Background Particles */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}>
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              width: '2px',
              height: '2px',
              background: '#7CFC00',
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.6,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      <motion.form 
        onSubmit={login} 
        style={{ 
          maxWidth: '450px', 
          width: '100%',
          padding: '2.5rem', 
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '20px',
          border: '1px solid rgba(124, 252, 0, 0.2)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 0 30px rgba(124, 252, 0, 0.1)',
          position: 'relative',
          zIndex: 10
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
      >
        <motion.div 
          className="title"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <img src="/logo.png" className="logo-rect" alt="StatPulseAI Logo" style={{ height: '100px', marginBottom: '0.1rem' }} />
        </motion.div>
        
        <motion.h2 
          style={{ fontSize: '2rem', marginBottom: '1.2rem', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: 'white' }}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          Login
        </motion.h2>
        
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: 'rgba(255, 0, 0, 0.1)',
              border: '1px solid rgba(255, 0, 0, 0.3)',
              borderRadius: '8px',
              padding: '0.8rem',
              marginBottom: '1rem',
              color: '#ff6b6b',
              fontSize: '0.9rem'
            }}
          >
            {error}
            {error.includes('verify your email') && (
              <div style={{ marginTop: '0.5rem' }}>
                <Link to="/register" style={{ color: '#7CFC00', textDecoration: 'none' }}>
                  Need to register? Click here
                </Link>
              </div>
            )}
          </motion.div>
        )}
        
        <motion.input
          placeholder="Email"
          onChange={e => setEmail(e.target.value)}
          style={{ width: '100%', padding: '0.8rem', marginBottom: '0.8rem', fontSize: '1rem', borderRadius: '8px', border: '1px solid #ccc', boxSizing: 'border-box', background: 'rgba(255, 255, 255, 0.9)', color: '#333' }}
          required
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        />
        
        <motion.input
          placeholder="Password"
          type="password"
          onChange={e => setPassword(e.target.value)}
          style={{ width: '100%', padding: '0.8rem', marginBottom: '0.8rem', fontSize: '1rem', borderRadius: '8px', border: '1px solid #ccc', boxSizing: 'border-box', background: 'rgba(255, 255, 255, 0.9)', color: '#333' }}
          required
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
        />
        
        <motion.button
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%',
            backgroundColor: isLoading ? '#666' : '#7CFC00',
            color: '#000',
            fontWeight: 'bold',
            padding: '0.8rem',
            borderRadius: '8px',
            border: 'none',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '1.1rem',
            marginBottom: '0.8rem'
          }}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          whileHover={!isLoading ? { scale: 1.02, boxShadow: '0 0 20px rgba(124, 252, 0, 0.4)' } : {}}
          whileTap={!isLoading ? { scale: 0.98 } : {}}
        >
          {isLoading ? 'Logging in...' : 'Login'}
        </motion.button>
        
        <motion.div 
          style={{ marginBottom: '0.8rem' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.4 }}
        >
          <Link to="/register" style={{ marginRight: '1rem', color: '#7CFC00', fontSize: '1rem', textDecoration: 'none' }}>
            No account? Register
          </Link>
          <Link to="/forgot-password" style={{ color: '#7CFC00', fontSize: '1rem', textDecoration: 'none' }}>
            Forgot Password?
          </Link>
        </motion.div>
        
        {/* Go Back Button */}
        <motion.button
          type="button"
          onClick={() => navigate('/')}
          style={{
            backgroundColor: '#001c3d',
            color: '#7CFC00',
            padding: '0.6rem 1.5rem',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.6 }}
          whileHover={{ scale: 1.05, boxShadow: '0 0 15px rgba(0, 28, 61, 0.6)' }}
          whileTap={{ scale: 0.95 }}
        >
          Main Page
        </motion.button>
      </motion.form>
    </motion.div>
  );
}
